import flowTypes from 'new/components/flows/types/flowTypes';
import { DisplayOrderable, sortByDisplayOrder } from 'new/utils/displayOrder';

/**
 * Action endpoints (vendor_endpoint.is_action) perform a write — e.g. "Create a note" — instead
 * of fetching data. They're offered as API Server flow targets, but hidden from ETL source forms
 * (Flex Flow, DirectFlow, ELT, ...), where a scheduled source would repeat the write every run.
 *
 * A missing is_action means false, so nothing is hidden for data that predates the field.
 * Endpoints in `keepIds` (the source's saved endpoint) are never hidden, so editing an existing
 * source still shows the endpoint it uses even if it was later marked as an action.
 */
type EndpointId = number | string | null | undefined;

type ActionEndpointFilterOptions = {
  flowType?: string | null;
  keepIds?: EndpointId[];
};

/**
 * Single definition of "is an action". Only a real `true` counts: anything else (missing, null,
 * or an unexpected value) leaves the endpoint visible, which is how forms behaved before
 * is_action existed — so a malformed value can never hide a usable endpoint.
 */
export const isActionEndpoint = (endpoint: { is_action?: unknown } | null | undefined) =>
  endpoint?.is_action === true;

export const showsActionEndpoints = (flowType?: string | null) => flowType === flowTypes.api_server;

function filterActions<T>(
  items: T[] | null | undefined,
  getId: (item: T) => EndpointId,
  isAction: (item: T) => boolean,
  { flowType, keepIds = [] }: ActionEndpointFilterOptions,
): T[] {
  const list = items ?? [];
  if (showsActionEndpoints(flowType)) return list;

  const kept = new Set(keepIds.filter((id) => id != null).map(String));
  return list.filter((item) => !isAction(item) || kept.has(String(getId(item))));
}

/** Filters vendor endpoint entities (objects with `id` and `is_action`). */
export function filterActionEndpoints<T extends { id?: EndpointId; is_action?: boolean | null }>(
  endpoints: T[] | null | undefined,
  options: ActionEndpointFilterOptions = {},
): T[] {
  return filterActions(endpoints, (e) => e.id, isActionEndpoint, options);
}

/** Filters `vendor_endpoint_id` select options (`{ value, is_action }`, see the paging reducer). */
export function filterActionEndpointOptions<
  T extends { value?: EndpointId; is_action?: boolean | null },
>(selectOptions: T[] | null | undefined, options: ActionEndpointFilterOptions = {}): T[] {
  return filterActions(selectOptions, (o) => o.value, isActionEndpoint, options);
}

/**
 * Flow type to filter with. API Server targets are recognised either by their flow type or by
 * being created/edited under a parent Nexset (`isAPIServerTarget`); either signal is enough.
 */
export const getEndpointFlowType = ({
  flowType,
  isAPIServerTarget,
}: {
  flowType?: string | null;
  isAPIServerTarget?: boolean;
}) => (isAPIServerTarget ? flowTypes.api_server : flowType);

/**
 * Endpoint ids an edited source already uses: the saved `vendor_endpoint` (an object or an id),
 * `vendor_endpoint_id`, and the form's current selection.
 */
export function getSourceEndpointIds({
  sourceBeingEdited,
  selectedEndpointId,
}: {
  sourceBeingEdited?: { vendor_endpoint?: unknown; vendor_endpoint_id?: EndpointId } | null;
  selectedEndpointId?: EndpointId;
}): EndpointId[] {
  const savedEndpoint = sourceBeingEdited?.vendor_endpoint;
  const savedEndpointId =
    savedEndpoint && typeof savedEndpoint === 'object'
      ? (savedEndpoint as { id?: EndpointId }).id
      : (savedEndpoint as EndpointId);
  return [savedEndpointId, sourceBeingEdited?.vendor_endpoint_id, selectedEndpointId];
}

/** Endpoint dropdown options for a templatized API source form (Flex Flow, DirectFlow, API Server). */
export function getSourceEndpointOptions<
  T extends { value?: EndpointId; is_action?: boolean | null },
>(
  selectOptions: T[] | null | undefined,
  {
    flowType,
    isAPIServerTarget,
    sourceBeingEdited,
    selectedEndpointId,
  }: {
    flowType?: string | null;
    isAPIServerTarget?: boolean;
    sourceBeingEdited?: { vendor_endpoint?: unknown; vendor_endpoint_id?: EndpointId } | null;
    selectedEndpointId?: EndpointId;
  },
): T[] {
  return filterActionEndpointOptions(selectOptions, {
    flowType: getEndpointFlowType({ flowType, isAPIServerTarget }),
    keepIds: getSourceEndpointIds({ sourceBeingEdited, selectedEndpointId }),
  });
}

type TemplatedEndpoint = {
  id?: EndpointId;
  is_action?: boolean | null;
  source_template?: { endpoint_type?: string } | null;
};

/** Endpoints that return data, as opposed to discovery endpoints (used by ELT / API multi). */
const isDataEndpoint = (endpoint: TemplatedEndpoint) => {
  const endpointType = endpoint?.source_template?.endpoint_type;
  return endpointType !== 'discovery' && endpointType !== 'discovery_fields';
};

/**
 * Default endpoint for a new ELT / templatized API multi source: the first data endpoint in
 * display order, skipping action endpoints unless this is an API Server target.
 */
export function getDefaultDataEndpoint<T extends TemplatedEndpoint & DisplayOrderable>(
  endpoints: T[] | null | undefined,
  { flowType }: { flowType?: string | null } = {},
): T | undefined {
  return filterActionEndpoints(sortByDisplayOrder(endpoints), { flowType }).find(isDataEndpoint);
}

/**
 * Feed endpoints for ELT: data endpoints, without action endpoints except one the source uses.
 * Keeps the input order.
 */
export function getFeedEndpoints<T extends TemplatedEndpoint>(
  endpoints: T[] | null | undefined,
  { keepIds = [] }: { keepIds?: EndpointId[] } = {},
): T[] {
  return filterActionEndpoints(endpoints, { keepIds }).filter(isDataEndpoint);
}
